import useSidebarStore from '@/stores/useSidebarStore';
import React, { Suspense, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { trackEvent } from '@/utils/analytics';
import { EventAction } from '@/types/analytics';
import { lazyWithRetry } from '@/utils/lazyWithRetry';

const WalletList = lazyWithRetry(() => import('./components/WalletList'));
const WalletDetails = lazyWithRetry(() => import('./components/WalletDetails'));
const WalletSeedPhrase = lazyWithRetry(() => import('./components/WalletSeedPhrase'));

export const sectionComponents = {
  walletList: WalletList,
  walletDetails: WalletDetails,
  walletSeedPhrase: WalletSeedPhrase,
};

const slideVariants = {
  initial: (direction: 'forward' | 'backward') => ({
    x: direction === 'forward' ? '100%' : '-100%',
  }),
  animate: {
    x: 0,
  },
  exit: (direction: 'forward' | 'backward') => ({
    x: direction === 'forward' ? '-100%' : '100%',
  }),
};

type ScreenStackItem = {
    screenId: keyof typeof sectionComponents;
    params?: Record<string, any>;
};

function SidebarWalletManagement() {
    const isOpen = useSidebarStore(state => state.isOpenWalletManagement);
    const setIsOpenWalletManagement = useSidebarStore(state => state.setIsOpenWalletManagement);
    const [stack, setStack] = useState<ScreenStackItem[]>([{ screenId: 'walletList' }]);
    const [direction, setDirection] = useState('forward');
    const currentStackItem = stack[stack.length - 1];
    const currentScreen = currentStackItem.screenId;

    // Reset navigation stack when sidebar closes
    useEffect(() => {
        if (!isOpen) {
            setTimeout(() => {
                setStack([{ screenId: 'walletList' }]);
                setDirection('forward');
            }, 300);
        }
    }, [isOpen]);

    const navigateTo = (screenId: keyof typeof sectionComponents, params?: Record<string, any>) => {
        if (stack.some(item => item.screenId === screenId)) return;
        setDirection('forward');
        setStack((prev) => [...prev, { screenId, params }]);
    };

    const goBack = () => {
        if (stack.length <= 1) return;

        // Get previous screen (destination after going back)
        const previousScreen = stack[stack.length - 2];

        // Track back navigation
        trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
            label: 'wallet_management_go_back',
            component: 'wallet_management_sidebar',
            current_screen: currentScreen,
            destination_screen: previousScreen.screenId,
            stack_depth: stack.length,
            wallet_address: currentStackItem.params?.wallet?.publicKey || null,
            wallet_type: currentStackItem.params?.wallet?.walletType || null,
        });

        setDirection('backward');
        setStack((prev) => prev.slice(0, -1));
    };

    const handleSidebarClose = (open: boolean) => {
        if (!open) {
            // Track sidebar close event
            trackEvent('ui_interaction', EventAction.MODAL_CLOSE, {
                label: 'wallet_management_sidebar_close',
                component: 'wallet_management_sidebar',
                current_screen: currentScreen,
                stack_depth: stack.length,
                wallet_address: currentStackItem.params?.wallet?.publicKey || null,
                wallet_type: currentStackItem.params?.wallet?.walletType || null,
            });
        }
        setIsOpenWalletManagement(open);
    };

    const ScreenComponent = sectionComponents[currentScreen as keyof typeof sectionComponents];

    return (
        <Sheet open={isOpen} onOpenChange={handleSidebarClose}>
            <SheetContent
                side="right"
                className="bg-[#171921] border-l border-gray-800 w-full md:w-[31.25rem] overflow-hidden p-0 [&>button]:hidden"
            >
                <div className="relative w-full h-full overflow-hidden">
                    <AnimatePresence initial={false} mode="sync" custom={direction}>
                        <motion.div
                            key={currentScreen}
                            className="absolute top-0 left-0 right-0 bottom-0 overflow-auto px-7 py-9"
                            variants={slideVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            custom={direction}
                        >
                            <Suspense fallback={<></>}>
                                <ScreenComponent
                                    wallet={currentStackItem.params?.wallet}
                                    goBack={goBack}
                                    navigateTo={navigateTo}
                                    handleSidebarClose={handleSidebarClose}
                                    {...(currentStackItem.params || {})}
                                />
                            </Suspense>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </SheetContent>
        </Sheet>
    )
}

export default SidebarWalletManagement;